import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Dropdown } from 'antd';
import { CopyOutlined, DownOutlined } from '@ant-design/icons';
import useApiClient from '../../hooks/api/useApiClient';

export const OnboardingLinkButtonMode = {
  Redirect: 'redirect',
  Copy: 'copy',
};

export default function OnboardingLinkButton({
  paymentGateway,
  paymentGateways,
  text,
  type,
  className,
  mode = OnboardingLinkButtonMode.Redirect,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Buttons:OnboardingLinkButton' });
  const { MerchantProfileManager } = useApiClient();

  const [loading, setLoading] = useState(false);

  const gateways = paymentGateway ? [paymentGateway] : (paymentGateways ?? []);
  const isMultiGateway = gateways.length > 1;

  async function handleAction(gateway) {
    if (!gateway) {
      return null;
    }

    if (mode === OnboardingLinkButtonMode.Copy) {
      // ClipboardItem must be created synchronously during the user gesture
      // so mobile Safari preserves the gesture context for clipboard access.
      // The data blob is provided as a Promise that resolves after the API call.
      const dataPromise = MerchantProfileManager.createOnboardingLink({
        payment_gateway: gateway,
        return_url: window.location.href,
      });

      try {
        setLoading(true);
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': dataPromise.then(({ data, success }) => {
              if (!success || !data?.url) {
                throw new Error('Failed to create link');
              }

              return new Blob([data.url], { type: 'text/plain' });
            }),
          }),
        ]);
        setLoading(false);

        return toast.success(t('toasts.link_copied'));
      } catch {
        setLoading(false);

        return toast.error(t('toasts.copy_failed'));
      }
    }

    setLoading(true);
    const { data, success } = await MerchantProfileManager.createOnboardingLink({
      payment_gateway: gateway,
      return_url: window.location.href,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_create', { paymentGateway: gateway }));
    }

    return window.location.replace(data?.url);
  }

  if (isMultiGateway) {
    return (
      <Dropdown
        trigger={['click']}
        menu={{
          items: gateways.map((gateway) => ({
            key: gateway,
            label: t('text', { paymentGateway: gateway }),
            icon: mode === OnboardingLinkButtonMode.Copy ? <CopyOutlined /> : undefined,
            onClick: () => handleAction(gateway),
          })),
        }}
      >
        <Button
          type={type ?? 'link'}
          loading={loading}
          className={className}
          icon={mode === OnboardingLinkButtonMode.Copy ? <CopyOutlined /> : undefined}
        >
          {text ?? t('text_generic')} <DownOutlined />
        </Button>
      </Dropdown>
    );
  }

  return (
    <Button
      type={type ?? 'link'}
      onClick={() => handleAction(gateways[0])}
      loading={loading}
      className={className}
      icon={mode === OnboardingLinkButtonMode.Copy ? <CopyOutlined /> : undefined}
    >
      {text ?? t('text', { paymentGateway: gateways[0] })}
    </Button>
  );
}
